/**
 * Ported from reporting_app/web/src/components/ui/Toast.tsx. The source's
 * `animate-in slide-in-from-right` classes depend on the tailwindcss-animate
 * plugin, which neither app has installed (they're no-ops there too) —
 * dropped here rather than carrying dead classes forward.
 *
 * =========================================================================
 * THE CONTAINER ANNOUNCES (P36, S1-06)
 * =========================================================================
 * Until P36 the container was a plain `<div>`: no `aria-live`, no `role`. **14
 * error toasts are the only feedback the app gives for a mutation** — a list
 * that fails to load has a banner, a save that fails has this and nothing else
 * — so a reader who was not looking at the bottom right corner was told
 * nothing at all. WCAG 2.2 AA 4.1.3.
 *
 * Two regions, not one, and the reason is the politeness setting: an error
 * interrupts (`role="alert"`, which is `aria-live="assertive"` by definition),
 * a confirmation waits its turn (`aria-live="polite"`). One region cannot be
 * both, and a region whose politeness changes as messages arrive is a region
 * screen readers announce unpredictably. So the errors live in their own
 * `role="alert"` region and everything else in the polite one, both visually in
 * the same stack.
 *
 * `aria-live` regions announce what is INSERTED into them, which is why the two
 * regions are always mounted and only their contents change. A region that
 * appears at the same moment as its first message announces nothing.
 */
import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { IconButton } from './Button';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
    id: number;
    message: string;
    type: ToastType;
    sticky: boolean;
}

export interface ToastOptions {
    /**
     * Stays until the reader closes it.
     *
     * Bruno, testing the pilot on 2026-08-28: *"The message card should stay
     * visible till I close it."* He was looking at the compose result, which is
     * a sentence explaining what the agent did or refused to do — three and a
     * half seconds is enough to notice a card and not enough to read one.
     *
     * The distinction is not severity but whether the message CARRIES
     * something. "Status updated" is a confirmation of an action the user just
     * took and can fade; anything that tells them something they did not
     * already know waits for them. Errors are sticky by default for that
     * reason — an error nobody could read is the same as no error at all.
     */
    sticky?: boolean;
}

interface ToastContextValue {
    toast: (message: string, type?: ToastType, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within ToastProvider');
    return ctx;
}

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = useCallback((message: string, type: ToastType = 'info', options?: ToastOptions) => {
        const id = ++nextId;
        setToasts(prev => [...prev, { id, message, type, sticky: options?.sticky ?? type === 'error' }]);
    }, []);

    const removeToast = useCallback((id: number) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ toast: addToast }}>
            {children}
            <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex max-w-sm flex-col gap-2">
                <div role="alert" className="flex flex-col gap-2">
                    {toasts.filter(t => t.type === 'error').map(t => (
                        <ToastItem key={t.id} toast={t} onDismiss={removeToast} />
                    ))}
                </div>
                <div role="status" aria-live="polite" className="flex flex-col gap-2">
                    {toasts.filter(t => t.type !== 'error').map(t => (
                        <ToastItem key={t.id} toast={t} onDismiss={removeToast} />
                    ))}
                </div>
            </div>
        </ToastContext.Provider>
    );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
    useEffect(() => {
        if (toast.sticky) return;
        // Reference PD16: a confirmation goes after 4 seconds.
        const timer = setTimeout(() => onDismiss(toast.id), 4000);
        return () => clearTimeout(timer);
    }, [toast.id, toast.sticky, onDismiss]);

    const Icon = toast.type === 'success' ? CheckCircle : toast.type === 'error' ? AlertCircle : Info;
    const colors = toast.type === 'success'
        ? 'border-brand-100 bg-brand-50 text-brand-700'
        : toast.type === 'error'
        ? 'border-err-edge bg-err-50 text-err'
        : 'border-info-100 bg-info-50 text-info';

    return (
        <div className={`pointer-events-auto flex items-start gap-2 rounded-card border px-4 py-3 shadow-overlay ${colors}`}>
            <Icon aria-hidden="true" className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <p className="flex-1 text-sm">{toast.message}</p>
            <IconButton label="Dismiss" variant="inherit" onClick={() => onDismiss(toast.id)} className="-my-1 -mr-2">
                <X className="h-4 w-4" />
            </IconButton>
        </div>
    );
}
